import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { rowOf } from "../gridSize";
import { CELL_FIRE_TUNING } from "../tuning/featureTuning";
import type { Point } from "./flightPath";
import { ART_SCALE, CELL_FIRE_LAYOUT } from "./layout";

/**
 * The artist's burning cell (`cell_fire.riv`'s `CellFire`, played as its Spine conversion): its boolean `lit` raises a
 * fire from the cell's bottom edge (0.3 s), an orange glow along its inner edges, flames along its bottom and rising
 * embers, burning on a loop; off, it dies down (0.4 s) and draws nothing.
 */
export const CELL_FIRE_RIVE = { lit: "lit" } as const;

/** What a burning cell asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface CellFireArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setBoolean(name: string, value: boolean): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

interface Fire {
  art: CellFireArt;
  sprite: Sprite;
  /** The cell it burns on (null: put back, or dying down). */
  cell: number | null;
  /** Engine seconds of its dying down still to draw. */
  fading: number;
}

/** The grid's middle row, whose window is taller. */
const MIDDLE_ROW = 1;

/**
 * The cells that hold a multiplier burning (the artist's handoff): a fire on each, lit as MULTI's fireball reaches the
 * cell, burning while the multiplier waits there, dying down when the bonus ends. In the grid, over the cells and the
 * frame, under the coins: a little bigger than its cell, its flames over the frame's bars. A few fires (the file loaded
 * once), each on its cell's centre, the middle row's stretched to its taller window; drawn on the UI's runner
 * while it burns and dies down, at the tuning's frame rate, then hidden and put back: never work a round waits for.
 * Nothing burns before they are in, nor on a cell past the tuning's count.
 */
export class CellFires extends Container {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly cellCentre: (index: number) => Point;
  private fires: Fire[] | null = null;
  private drawing = false;
  /** Device pixels per design unit where they burn (their textures' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log; cellCentre: (index: number) => Point }) {
    super({ label: "CellFires" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
    this.cellCentre = input.cellCentre;
  }

  /** The artist's fires, loaded: hidden until a cell is lit. */
  public setArt(arts: readonly CellFireArt[]): void {
    if (this.destroyed || this.fires) {
      for (const art of arts) art.dispose();
      return;
    }
    this.fires = arts.map((art) => {
      art.resize(this.textureScale());
      const sprite = new Sprite({ texture: art.texture, anchor: 0.5, label: "CellFire" });
      sprite.visible = false;
      this.addChild(sprite);
      return { art, sprite, cell: null, fading: 0 };
    });
    this.log("[CellFires] the cell fires are in");
  }

  /** Whether they are in. */
  public get ready(): boolean {
    return this.fires !== null;
  }

  /** Whether they are in, and the cells burning now (the dev hook's read). */
  public get state(): { loaded: boolean; burning: number[] } {
    const burning = (this.fires ?? []).flatMap((fire) => (fire.cell === null ? [] : [fire.cell]));
    return { loaded: this.fires !== null, burning: burning.sort((a, b) => a - b) };
  }

  /** Device pixels per design unit where they burn: their textures are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    for (const fire of this.fires ?? []) fire.art.resize(this.textureScale());
  }

  /** Cell `index` catches fire (it rises, then burns on); burning already, it burns on; with no fire free, none. */
  public light(index: number): void {
    const fires = this.fires;
    if (!fires || this.destroyed || fires.some((fire) => fire.cell === index)) return;
    const fire = fires.find((one) => one.cell === null && one.fading <= 0);
    if (!fire) return;
    const { fire: artboard, fireScale, middleRowStretch } = CELL_FIRE_LAYOUT;
    const at = this.cellCentre(index);
    fire.cell = index;
    fire.sprite.position.set(at.x, at.y);
    const units = ART_SCALE * fireScale;
    fire.sprite.setSize(artboard.width * units, artboard.height * units * (rowOf(index) === MIDDLE_ROW ? middleRowStretch : 1));
    fire.art.setBoolean(CELL_FIRE_RIVE.lit, true);
    fire.art.render(0);
    fire.sprite.visible = true;
    this.draw();
  }

  /** Cell `index`'s fire dies down, then is put back (its multiplier gone into what the cell holds); none burning, nothing. */
  public douse(index: number): void {
    const fire = this.fires?.find((one) => one.cell === index);
    if (!fire) return;
    fire.cell = null;
    fire.fading = CELL_FIRE_TUNING.offSeconds;
    fire.art.setBoolean(CELL_FIRE_RIVE.lit, false);
    this.draw();
  }

  /** Every burning cell's fire dies down, then is put back (the bonus over: its multipliers gone). */
  public clear(): void {
    for (const fire of this.fires ?? []) {
      if (fire.cell === null) continue;
      fire.cell = null;
      fire.fading = CELL_FIRE_TUNING.offSeconds;
      fire.art.setBoolean(CELL_FIRE_RIVE.lit, false);
    }
    this.draw();
  }

  public override destroy(options?: DestroyOptions): void {
    const fires = this.fires;
    this.fires = null;
    for (const fire of fires ?? []) fire.art.dispose();
    super.destroy(options);
  }

  /** Texture pixels per artboard pixel: its size on the screen, at the tuning's share. */
  private textureScale(): number {
    return ART_SCALE * CELL_FIRE_LAYOUT.fireScale * this.pixelsPerUnit * CELL_FIRE_TUNING.resolution;
  }

  /** Whether any fire burns or dies down. */
  private get busy(): boolean {
    return this.fires?.some((fire) => fire.cell !== null || fire.fading > 0) ?? false;
  }

  /** The burning fires drawn at their own pace; each dying down hidden once it is out. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          for (const fire of this.fires ?? []) {
            if (fire.cell === null && fire.fading <= 0) continue;
            fire.art.advance(delta);
            if (fire.cell !== null) continue;
            fire.fading = Math.max(0, fire.fading - delta);
            if (fire.fading === 0) fire.sprite.visible = false;
          }
        },
        () => this.destroyed || !this.busy,
      )
      .then(() => {
        this.drawing = false;
        // One lit as the last went out.
        if (!this.destroyed && this.busy) this.draw();
      });
  }
}
